<script>
import debounce from 'lodash/debounce';
import AdminMergeDialog from '@/components/admin/AdminMergeDialog';

export default {
  name: 'AdminUserLookup',
  components: {
    AdminMergeDialog
  },
  props: {
    errors: Array
  },
  data() {
    return {
      isLoading: false,
      search: '',
      users: [],
      mergePrimary: null,
      mergeDialogVisible: false
    };
  },
  methods: {
    focus() {
      this.$refs.search.focus();
    },

    handleSearch: debounce(function () {
      if (this.search) {
        // Fix common copy + paste errors
        const search = this.search.trim().replace(/^mailto:/, '');

        this.isLoading = true;
        this.$http
          .get('/admin/users', {
            params: {
              search,
              sortBy: 'role'
            }
          })
          .then(res => (this.users = res.data.users))
          .catch(err => {
            this.$notify({
              type: 'error',
              title: this.$t('Error'),
              message: err.response.data.message
            });
          })
          .finally(() => (this.isLoading = false));
      } else {
        this.users = [];
      }
    }, 250),

    sudoLogin(user) {
      this.$http
        .post(`/admin/users/${user.id}/sudo`)
        .then(() => (location.href = '/')) // trigger reload
        .catch(err => {
          this.$notify({
            type: 'error',
            title: this.$t('Error'),
            message: err.response.data.message
          });
        });
    },

    viewInStripe(user) {
      if (user) {
        window.open(`https://dashboard.stripe.com/customers/${user.account.stripeCustomerId}`);
      }
    },

    // Opens the merge dialog with this user's account as the primary (kept) account
    mergeAccounts(user) {
      this.mergePrimary = user;
      this.mergeDialogVisible = true;
    },

    handleMerged(secondaryId) {
      this.users = this.users.filter(user => user.accountId !== secondaryId);
    },

    deleteAccount(user) {
      const accountId = user.accountId;
      const namePlural = /s$/i.test(user.name) ? `${user.name}’` : `${user.name}’s`;

      this.$prompt(`Please enter your password to permanently delete ${namePlural} account.`, {
        title: 'Delete Account',
        confirmButtonText: 'Delete',
        confirmButtonClass: 'el-button--danger',
        cancelButtonText: 'Cancel',
        inputType: 'password'
      }).then(result => {
        const password = result.value;

        // Delete the account
        this.$http
          .delete(`/admin/accounts/${accountId}`, {
            data: { password }
          })
          .then(() => {
            // Update the user list
            this.users = this.users.filter(user => user.accountId !== accountId);
          })
          .catch(err => {
            this.$notify({
              type: 'error',
              title: this.$t('Error'),
              message: err.response.data.message
            });
          });
      });
    }
  }
};
</script>

<template>
  <div class="user-lookup">
    <el-row :gutter="20">
      <el-col :xs="24" :sm="24" :md="24" :lg="18" :xl="12">
        <el-input
          ref="search"
          v-model="search"
          :prefix-icon="isLoading ? 'el-icon-fa-circle-notch el-icon-fa-spin' : 'el-icon-fa-search'"
          placeholder="Search Users"
          class="search-input"
          clearable
          @input="handleSearch"
        />
      </el-col>
    </el-row>

    <table v-if="users.length" class="table">
      <thead>
        <th>Role</th>
        <th>User</th>
        <th class="hidden-sm-and-down">Joined</th>
        <th />
      </thead>
      <tbody>
        <tr v-for="user in users" :key="user.id">
          <td>
            <span class="role">
              <span v-if="user.isOwner">Owner</span>
              <span v-else-if="user.isAdmin">Admin</span>
              <span v-else>Client</span>
            </span>
          </td>
          <td>
            {{ user.name }}
            <span
              :class="{
                plan: true,
                'plan-personal': user.account.plan === 'personal',
                'plan-business': user.account.plan === 'business',
                'plan-agency': user.account.plan === 'agency'
              }"
            >
              {{ user.account.plan }} </span
            ><br />
            <small>
              <a :href="`mailto:${user.email}`">{{ user.email }}</a
              ><br />
              <span class="text-muted"> <i class="el-icon-fa-hashtag" />{{ user.accountId }} </span>
            </small>
          </td>
          <td class="hidden-sm-and-down">{{ $d(new Date(user.createdAt), 'short') }}<br /></td>
          <td>
            <el-dropdown trigger="click">
              <el-button size="small"> Options <i class="el-icon-fa-chevron-down el-icon--right" /> </el-button>
              <el-dropdown-menu slot="dropdown">
                <el-dropdown-item :disabled="user.id === $store.state.user.id" @click.native="sudoLogin(user)">
                  Sudo Login
                </el-dropdown-item>
                <el-dropdown-item :disabled="!user.account.stripeCustomerId" @click.native="viewInStripe(user)">
                  View in Stripe
                </el-dropdown-item>
                <el-dropdown-item v-if="user.isOwner" :divided="true" @click.native="mergeAccounts(user)">
                  Merge Accounts…
                </el-dropdown-item>
                <el-dropdown-item v-if="user.isOwner" @click.native="deleteAccount(user)">
                  Delete Account
                </el-dropdown-item>
              </el-dropdown-menu>
            </el-dropdown>
          </td>
        </tr>
      </tbody>
    </table>

    <div v-else class="no-users">No Results</div>

    <admin-merge-dialog :primary="mergePrimary" :visible.sync="mergeDialogVisible" @merged="handleMerged" />
  </div>
</template>

<style lang="scss" scoped>
@import '@/styles/variables.scss';

.search-input {
  margin-bottom: 1.5rem;
}

.no-users {
  font-size: 1.2rem;
  text-align: center;
  color: $--color-gray-40;
  padding: 5rem 0;
}

.plan {
  display: inline-block;
  border-radius: 0.25rem;
  color: $--color-white;
  font-size: 0.6rem;
  font-weight: 700;
  text-transform: uppercase;
  background-color: $--color-gray-60;
  padding: 0.125rem 0.25rem;
  vertical-align: middle;
  margin-top: -0.125rem;

  &.plan-personal {
    background-color: $--color-warning;
  }

  &.plan-business {
    background-color: $--color-success;
  }

  &.plan-agency {
    background-color: $--color-primary;
  }
}
</style>
